<template>
    <v-sheet>
        <v-toolbar flat dense color="secondary">
            <v-toolbar-title>Stock Journal Role</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-toolbar-items>
                <PIconBtn icon="mdi-content-save" tooltip="Save" @click="store"/>
                <PIconBtn icon="mdi-delete" tooltip="Delete" @click="confirmDeletion" v-if="editMode"/>
                <PIconBtn icon="mdi-sync" tooltip="Sync Data" :loading="loading" @click="refreshData"/>
            </v-toolbar-items>
        </v-toolbar>
        <v-divider></v-divider>
        <div class="pa-4 surface">
            <v-form ref="form">
                <v-row>
                    <v-col cols="4">
                        <v-row class="mb-2">
                            <v-col>
                                <v-text-field
                                    v-model="form.name"
                                    label="Role"
                                    outlined
                                    dense
                                    background-color="white"
                                    hide-details="auto">
                                </v-text-field>
                            </v-col>
                        </v-row>
                        <v-sheet outlined>
                            <v-row>
                                <v-col>
                                    <v-text-field
                                        class="pa-4"
                                        label="Search Users"
                                        outlined
                                        v-model="usersSearch"
                                        hide-details="auto"
                                        background-color="white"
                                        append-icon="mdi-magnify"
                                        dense>
                                    </v-text-field>
                                    <v-sheet outlined>
                                        <v-data-table
                                            :search="usersSearch"
                                            show-select
                                            v-model="form.users"
                                            dense
                                            :headers="usersHeaders"
                                            :items="users">
                                        </v-data-table>
                                    </v-sheet>
                                </v-col>
                            </v-row>
                        </v-sheet>
                    </v-col>
                    <v-col cols="8">
                        <v-sheet outlined>
                            <v-tabs v-model="tabs">
                                <v-tab>
                                    <v-icon left>mdi-warehouse</v-icon>
                                    <v-badge v-if="form.warehouses.length > 0" inline :content="form.warehouses.length">Warehouses</v-badge>
                                    <span v-else>Warehouses</span>
                                </v-tab>
                                <v-tab>
                                    <v-icon left>mdi-package</v-icon>
                                    <v-badge v-if="form.items.length > 0" inline :content="form.items.length">Items</v-badge>
                                    <span v-else>Items</span>
                                </v-tab>
                                <v-tab>
                                    <v-icon left>mdi-cash-register</v-icon>
                                    <v-badge v-if="form.accounts.length > 0" inline :content="form.accounts.length">Accounts</v-badge>
                                    <span v-else>Accounts</span>
                                </v-tab>
                            </v-tabs>
                            <v-divider></v-divider>
                            <v-tabs-items v-model="tabs">
                                <v-tab-item>
                                    <v-text-field
                                        class="pa-4"
                                        label="Search Warehouses"
                                        outlined
                                        v-model="warehousesSearch"
                                        hide-details="auto"
                                        background-color="white"
                                        append-icon="mdi-magnify"
                                        dense>
                                    </v-text-field>
                                    <v-sheet outlined>
                                        <v-data-table
                                            show-select
                                            :search="warehousesSearch"
                                            :items="warehouses"
                                            :headers="warehousesHeaders"
                                            v-model="form.warehouses"
                                            dense>
                                        </v-data-table>
                                    </v-sheet>
                                </v-tab-item>
                                <v-tab-item>
                                    <v-text-field
                                        class="pa-4"
                                        label="Search Items"
                                        outlined
                                        v-model="itemsSearch"
                                        hide-details="auto"
                                        background-color="white"
                                        append-icon="mdi-magnify"
                                        dense>
                                    </v-text-field>
                                    <v-sheet outlined>
                                        <v-data-table
                                            :search="itemsSearch"
                                            show-select
                                            v-model="form.items"
                                            dense
                                            :headers="itemsHeaders"
                                            :items="items">
                                        </v-data-table>
                                    </v-sheet>
                                </v-tab-item>
                                <v-tab-item>
                                    <v-text-field
                                        class="pa-4"
                                        label="Search Accounts"
                                        outlined
                                        v-model="accountsSearch"
                                        hide-details="auto"
                                        background-color="white"
                                        append-icon="mdi-magnify"
                                        dense>
                                    </v-text-field>
                                    <v-sheet outlined>
                                        <v-data-table
                                            v-model="form.accounts"
                                            :search="accountsSearch"
                                            show-select
                                            :headers="accountsHeaders"
                                            :items="accounts"
                                            dense>
                                        </v-data-table>
                                    </v-sheet>
                                </v-tab-item>
                            </v-tabs-items>
                        </v-sheet>
                    </v-col>
                </v-row>
            </v-form>
        </div>
        <v-divider></v-divider>
        <v-toolbar flat dense>
            <v-toolbar-items>
                <v-btn text @click="$emit('close', {snackbar: null})">Close</v-btn>
            </v-toolbar-items>
        </v-toolbar>
        <LoadingDialog :value="loading" message="Please Wait ..."/>
        <v-snackbar v-model="snackbar.display" bottom right :color="snackbar.color">{{snackbar.message}}</v-snackbar>
        <PAlert v-model="alert.display" v-bind="alert"/>
    </v-sheet>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { getIdempotentKey } from '../../services/stockJournal';
import PIconBtn from './../common/PIconBtn';
import LoadingDialog from './../common/LoadingDialog';
import PAlert from './../common/PAlert';

export default {
    name: "StockJournalRoleEdit",
    components: {  PIconBtn, LoadingDialog, PAlert },
    data() {
        return {

            /**
             * Headers
             */
            accountsHeaders: [
                { text: "Name", value: "display_name" }
            ],
            itemsHeaders: [
                { text: "Name", value: "display_name" }
            ],
            warehousesHeaders: [
                { text: "Name", value: "display_name" }
            ],
            usersHeaders: [
                { text: "Full Name", value: "full_name" },
                { text: "Username", value: "username" },
            ],

            /**
             * Search Fields
             */
            accountsSearch: "",
            warehousesSearch: "",
            itemsSearch: "",
            usersSearch: "",

            /**
             * role
             */
            form: {...this.role},

            initialAccounts: [],
            initialItems: [],
            initialWarehouses: [],
            initialUsers: [],

            /**
             * Other
             */
            idempotentKey: null,
            tabs: 0,
            loading: false,
            snackbar: { display: false, color: "", message: "" },
            alert: { display: false, title: "", content: "", callback: null },
        }
    },
    props: {
        role: Object
    },
    computed: {
        ...mapGetters({
            accounts: "stockJournal/account/accounts",
            warehouses: "stockJournal/warehouse/warehouses",
            items: "stockJournal/item/items",
            users: "stockJournal/user/users"
        }),
        editMode() {
            return !!this.form.id;
        }
    },
    methods: {
        ...mapActions("stockJournal/role", ["storeRole", "EditRole", "removeRole"]),
        store() {
            this.loading = true;
            if(!this.form.id) {
                this.storeRole({...this.form,  idempotentKey: this.idempotentKey})
                .then(response => {
                    let snackbar = { color: "success", message: response.data.message, display: true };
                    this.$emit("close", { snackbar });
                }).catch(error => {
                    let message = error.response ? error.response.data.message : error;
                    this.snackbar = { color: "error", message: message, display: true }
                }).finally(() => this.loading = false)
            } else {
                this.EditRole(this.form)
                    .then(response => {
                        let snackbar = { color: "success", message: response.data.message, display: true };
                        this.$emit("close", { snackbar });
                    }).catch(error => {
                        let message = error.response ? error.response.data.message : error;
                        this.snackbar = { color: "error", message, display: true }
                    }).finally(() => this.loading = false)
            }
        },
        fetchIdempotentKey() {
            getIdempotentKey().then(resp => this.idempotentKey = resp.data.key);
        },
        init() {
            if(this.accounts.length == 0) this.$store.dispatch("stockJournal/account/fetchAccounts");
            if(this.warehouses.length == 0) this.$store.dispatch("stockJournal/warehouse/fetchWarehouses");
            if(this.items.length == 0) this.$store.dispatch("stockJournal/item/fetchItems");
            if(this.users.length == 0) this.$store.dispatch("stockJournal/user/fetchUsers");

            this.form = {
                ...this.form,
                items: this.role.items.map(each => ({id: each})),
                accounts: this.role.accounts.map(each => ({id: each})),
                users: this.role.users.map(each => ({id: each})),
                warehouses: this.role.warehouses.map(each => ({id: each})),
            }
        },
        confirmDeletion() {
            this.alert = {
                display: true,
                title: "Please Confirm!",
                content: "Are you sure you want to delete this role!",
                callback: this.handleDelete
            };
        },
        handleDelete() {
            this.removeRole(this.form)
                .then(response => {
                    let snackbar = { color: "success", message: response.data.message, display: true };
                    this.$emit("close", { snackbar });
                }).catch(error => {
                    let message = error.response ? error.response.data.message : error;
                    this.snackbar = { color: "error", message, display: true }
                }).finally(() => this.loading = false)
        },
        async refreshData() {
            this.loading = true;
            await Promise.all([
                this.$store.dispatch("stockJournal/item/fetchItems"),
                this.$store.dispatch("stockJournal/warehouse/fetchWarehouses"),
                this.$store.dispatch("stockJournal/account/fetchAccounts"),
            ]);
            this.loading = false
        }
    },
    mounted() {

    },
    created() {
        this.init();
        this.fetchIdempotentKey();
    }
}
</script>

<style>

</style>